import React, { useEffect, useState, useRef } from 'react';
import { Button, Tag, Input, Select, InputNumber, Popconfirm, Space, Progress, message, Tooltip } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import http from '../api/http';

// 组织层级管理嵌入设置页 Tab
// 功能 1-5:
// 1 科室新增入口 (选择所属部门 + 名称 + 规划人数)
// 2 搜索过滤 (按名称关键字 + 部门筛选)
// 3 简单缓存 (TTL 60s)
// 4 在岗人数细化显示占用率 Progress
// 5 规划人数内联编辑 (单击数字进入编辑)

const CACHE_TTL = 60 * 1000; // 60 秒
const OrganizationHierarchyManager = () => {
  const [loading, setLoading] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const cacheRef = useRef({ ts:0, data: [] });
  const [expanded, setExpanded] = useState({});
  const [deptFilter, setDeptFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSection, setNewSection] = useState({ parent_id:null, name:'', planned_headcount:0 });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ parent_section_id:null, name:'', planned_headcount:0 });
  const [creatingPosition, setCreatingPosition] = useState(false);
  const [newPosition, setNewPosition] = useState({ parent_team_id:null, name:'', planned_headcount:0 });
  const [editingPlan, setEditingPlan] = useState(null); // { id, type, planned_headcount }
  const [savingPlan, setSavingPlan] = useState(false);
  const [newDept, setNewDept] = useState({ name:'', planned_headcount:0 });
  const [creatingDept, setCreatingDept] = useState(false);
  const [editingName, setEditingName] = useState(null); // { id, type, name }
  const [savingName, setSavingName] = useState(false);

  const occupancyRate = (current, planned) => {
    if (!planned || planned <=0) return 0;
    return Math.min(100, Math.round(current / planned * 100));
  };

  const fetchHierarchy = async (force=false) => {
    if(!force){
      const now = Date.now();
      if (cacheRef.current.data.length && (now - cacheRef.current.ts) < CACHE_TTL){
        setHierarchy(cacheRef.current.data);
        return;
      }
    }
    setLoading(true);
    try {
      const { data } = await http.get('/org-meta/hierarchy');
      if(data && data.success){
        setHierarchy(data.hierarchy || []);
        cacheRef.current = { ts: Date.now(), data: data.hierarchy || [] };
      }
    } catch(e){ message.error(e.response?.data?.error || e.message || '加载层级失败'); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ fetchHierarchy(); }, []);

  const toggleKey = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const openInlineEdit = (id, type, current) => setEditingPlan({ id, type, planned_headcount: current });

  const savePlan = async () => {
    if(!editingPlan) return;
    setSavingPlan(true);
    try {
      await http.patch(`/org-meta/${editingPlan.id}`, { planned_headcount: editingPlan.planned_headcount });
      message.success('规划人数已更新');
      setEditingPlan(null);
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '保存失败'); }
    finally { setSavingPlan(false); }
  };

  const createSection = async () => {
    if(!newSection.parent_id || !newSection.name.trim()){
      message.warning('请选择部门并填写科室名称');
      return;
    }
    setCreatingSection(true);
    try {
      await http.post('/org-meta', { type:'section', parent_id: newSection.parent_id, name: newSection.name.trim(), planned_headcount: newSection.planned_headcount||0 });
      message.success('科室创建/更新成功');
      setNewSection({ parent_id:null, name:'', planned_headcount:0 });
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '创建失败'); }
    finally { setCreatingSection(false); }
  };

  const createTeam = async () => {
    if(!newTeam.parent_section_id || !newTeam.name.trim()){
      message.warning('请选择科室并填写班组名称');
      return;
    }
    setCreatingTeam(true);
    try {
      await http.post('/org-meta', { type:'team', parent_id: newTeam.parent_section_id, name: newTeam.name.trim(), planned_headcount: newTeam.planned_headcount||0 });
      message.success('班组创建/更新成功');
      setNewTeam({ parent_section_id:null, name:'', planned_headcount:0 });
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '创建失败'); }
    finally { setCreatingTeam(false); }
  };

  const createPosition = async () => {
    if(!newPosition.parent_team_id || !newPosition.name.trim()){
      message.warning('请选择班组并填写岗位名称');
      return;
    }
    setCreatingPosition(true);
    try {
      await http.post('/org-meta', { type:'position', parent_id: newPosition.parent_team_id, name: newPosition.name.trim(), planned_headcount: newPosition.planned_headcount||0 });
      message.success('岗位创建/更新成功');
      setNewPosition({ parent_team_id:null, name:'', planned_headcount:0 });
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '创建失败'); }
    finally { setCreatingPosition(false); }
  };

  const createDepartment = async () => {
    if(!newDept.name.trim()){
      message.warning('请输入部门名称'); return;
    }
    setCreatingDept(true);
    try {
      await http.post('/org-meta', { type:'department', name:newDept.name.trim(), planned_headcount: newDept.planned_headcount||0 });
      message.success('部门创建/更新成功');
      setNewDept({ name:'', planned_headcount:0 });
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '创建失败'); }
    finally { setCreatingDept(false); }
  };

  const openNameEdit = (id,type,currentName) => setEditingName({ id, type, name: currentName });
  const cancelNameEdit = () => setEditingName(null);
  const saveName = async () => {
    if(!editingName || !editingName.name.trim()) { message.warning('名称不能为空'); return; }
    setSavingName(true);
    try {
      await http.patch(`/org-meta/${editingName.id}`, { name: editingName.name.trim() });
      message.success('名称已更新');
      setEditingName(null);
      fetchHierarchy(true);
    } catch(e){ message.error(e.response?.data?.error || e.message || '更新失败'); }
    finally { setSavingName(false); }
  };

  // 过滤逻辑
  const filtered = hierarchy
    .filter(d => !deptFilter || String(d.id) === String(deptFilter))
    .map(d => ({
      ...d,
      sections: d.sections.filter(s => !keyword || s.name.includes(keyword) || d.name.includes(keyword))
    }))
    .filter(d => d.sections.length || (!keyword || d.name.includes(keyword)));

  const allSections = hierarchy.flatMap(d => d.sections.map(s => ({ label: `${d.name} / ${s.name}`, value: s.id })));
  const allTeams = hierarchy.flatMap(d => d.sections.flatMap(s => (s.teams||[]).map(t => ({ label: `${d.name} / ${s.name} / ${t.name}`, value: t.id }))));

  return (
    <div style={{ padding: '8px 0' }}>
      <Space wrap style={{ marginBottom:12 }}>
        <Select
          allowClear
          placeholder='按部门筛选'
          style={{ width:160 }}
          value={deptFilter || undefined}
          onChange={v=> setDeptFilter(v||'')}
          options={hierarchy.map(d=> ({ label:d.name, value:String(d.id) }))}
        />
        <Input
          allowClear
          placeholder='关键字(部门/科室)'
          style={{ width:180 }}
          value={keyword}
          onChange={e=> setKeyword(e.target.value)}
        />
        <Button size='small' onClick={()=> fetchHierarchy(true)} loading={loading}>强制刷新</Button>
      </Space>

      <div style={{ background:'#fafafa', padding:12, borderRadius:6, marginBottom:16 }}>
        <div style={{ fontSize:12, color:'#666', marginBottom:4 }}>新增科室</div>
        <Space wrap size={8} style={{ marginBottom:12 }}>
          <Select
            placeholder='选择所属部门'
            style={{ width:180 }}
            value={newSection.parent_id || undefined}
            onChange={v=> setNewSection(o=> ({ ...o, parent_id:v }))}
            options={hierarchy.map(d=> ({ label:d.name, value:d.id }))}
          />
          <Input
            placeholder='科室名称'
            style={{ width:160 }}
            value={newSection.name}
            onChange={e=> setNewSection(o=> ({ ...o, name:e.target.value }))}
          />
          <InputNumber
            placeholder='规划人数'
            min={0}
            value={newSection.planned_headcount}
            onChange={v=> setNewSection(o=> ({ ...o, planned_headcount:v }))}
          />
          <Button type='primary' size='small' loading={creatingSection} onClick={createSection}>新增/更新科室</Button>
        </Space>
        <div style={{ fontSize:12, color:'#666', marginBottom:4 }}>新增班组</div>
        <Space wrap size={8} style={{ marginBottom:12 }}>
          <Select
            placeholder='选择所属科室'
            style={{ width:220 }}
            value={newTeam.parent_section_id || undefined}
            onChange={v=> setNewTeam(o=> ({ ...o, parent_section_id:v }))}
            options={allSections}
            showSearch
            optionFilterProp='label'
          />
          <Input
            placeholder='班组名称'
            style={{ width:160 }}
            value={newTeam.name}
            onChange={e=> setNewTeam(o=> ({ ...o, name:e.target.value }))}
          />
          <InputNumber
            placeholder='规划人数'
            min={0}
            value={newTeam.planned_headcount}
            onChange={v=> setNewTeam(o=> ({ ...o, planned_headcount:v }))}
          />
          <Button size='small' type='primary' loading={creatingTeam} onClick={createTeam}>新增/更新班组</Button>
        </Space>
        <div style={{ fontSize:12, color:'#666', marginBottom:4 }}>新增岗位</div>
        <Space wrap size={8} style={{ marginBottom:12 }}>
          <Select
            placeholder='选择所属班组'
            style={{ width:260 }}
            value={newPosition.parent_team_id || undefined}
            onChange={v=> setNewPosition(o=> ({ ...o, parent_team_id:v }))}
            options={allTeams}
            showSearch
            optionFilterProp='label'
          />
          <Input
            placeholder='岗位名称'
            style={{ width:160 }}
            value={newPosition.name}
            onChange={e=> setNewPosition(o=> ({ ...o, name:e.target.value }))}
          />
          <InputNumber
            placeholder='规划人数'
            min={0}
            value={newPosition.planned_headcount}
            onChange={v=> setNewPosition(o=> ({ ...o, planned_headcount:v }))}
          />
          <Button size='small' type='primary' loading={creatingPosition} onClick={createPosition}>新增/更新岗位</Button>
        </Space>
        <div style={{ fontSize:12, color:'#666', margin:'4px 0' }}>新增部门</div>
        <Space wrap size={8}>
          <Input
            placeholder='部门名称'
            style={{ width:200 }}
            value={newDept.name}
            onChange={e=> setNewDept(o=> ({ ...o, name:e.target.value }))}
          />
          <InputNumber
            placeholder='规划人数'
            min={0}
            value={newDept.planned_headcount}
            onChange={v=> setNewDept(o=> ({ ...o, planned_headcount:v }))}
          />
          <Button size='small' type='dashed' loading={creatingDept} onClick={createDepartment}>新增/更新部门</Button>
        </Space>
      </div>

      {filtered.map(dept => {
        const deptKey = `dept-${dept.id}`;
        const openDept = expanded[deptKey];
        const deptRate = occupancyRate(dept.current_headcount, dept.planned_headcount);
        return (
          <div key={dept.id} style={{ border:'1px solid #e5e7eb', borderRadius:6, marginBottom:10 }}>
            <div style={{ padding:10, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:220 }}>
                <Button type='text' size='small' onClick={()=> toggleKey(deptKey)} style={{ fontWeight:600 }}>
                  {openDept ? '▾':'▸'}
                </Button>
                {editingName && editingName.id===dept.id ? (
                  <Space size={4}>
                    <Input
                      size='small'
                      style={{ width:140 }}
                      value={editingName.name}
                      onChange={e=> setEditingName(n=> ({ ...n, name:e.target.value }))}
                    />
                    <Tooltip title='保存'>
                      <Button icon={<SaveOutlined />} size='small' type='primary' loading={savingName} onClick={saveName} />
                    </Tooltip>
                    <Tooltip title='取消'>
                      <Button icon={<CloseOutlined />} size='small' onClick={cancelNameEdit} />
                    </Tooltip>
                  </Space>
                ) : (
                  <Space size={4}>
                    <span style={{ fontWeight:600 }}>{dept.name}</span>
                    <Tooltip title='重命名'>
                      <Button icon={<EditOutlined />} size='small' type='text' onClick={()=> openNameEdit(dept.id,'department',dept.name)} />
                    </Tooltip>
                  </Space>
                )}
                <Tag color='blue'>在岗 {dept.current_headcount}</Tag>
                <Tag color='geekblue' onClick={()=> openInlineEdit(dept.id,'department',dept.planned_headcount)} style={{ cursor:'pointer' }}>规划 {dept.planned_headcount}</Tag>
                <div style={{ width:140 }}>
                  <Progress percent={deptRate} size='small' status={deptRate>100?'exception':'active'} strokeColor={deptRate>100?'#ff4d4f':'#1677ff'} showInfo={false} />
                </div>
                <span style={{ fontSize:12, color:'#555' }}>{deptRate}%</span>
              </div>
            </div>
            {openDept && (
              <div style={{ padding:'4px 16px 12px 32px' }}>
                {dept.sections.length ? dept.sections.map(sec => {
                  const secKey = `sec-${sec.id}`;
                  const openSec = expanded[secKey];
                  const secRate = occupancyRate(sec.current_headcount, sec.planned_headcount);
                  return (
                    <div key={sec.id} style={{ display:'flex', flexDirection:'column', padding:'4px 0', borderBottom:'1px dashed #f1f5f9', gap:4 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:200, flexWrap:'wrap' }}>
                        <Button type='text' size='small' onClick={()=> toggleKey(secKey)} style={{ fontWeight:500, padding:'0 4px' }}>{openSec ? '▾':'▸'}</Button>
                        {editingName && editingName.id===sec.id ? (
                          <Space size={4}>
                            <Input
                              size='small'
                              style={{ width:120 }}
                              value={editingName.name}
                              onChange={e=> setEditingName(n=> ({ ...n, name:e.target.value }))}
                            />
                            <Button icon={<SaveOutlined />} size='small' type='primary' loading={savingName} onClick={saveName} />
                            <Button icon={<CloseOutlined />} size='small' onClick={cancelNameEdit} />
                          </Space>
                        ) : (
                          <Space size={4}>
                            <span>{sec.name}</span>
                            <Button icon={<EditOutlined />} size='small' type='text' onClick={()=> openNameEdit(sec.id,'section',sec.name)} />
                          </Space>
                        )}
                        <Tag color='green'>在岗 {sec.current_headcount}</Tag>
                        <Tag color='gold' onClick={()=> openInlineEdit(sec.id,'section',sec.planned_headcount)} style={{ cursor:'pointer' }}>规划 {sec.planned_headcount}</Tag>
                        <div style={{ width:120 }}>
                          <Progress percent={secRate} size='small' status={secRate>100?'exception':'active'} strokeColor={secRate>100?'#ff4d4f':'#52c41a'} showInfo={false} />
                        </div>
                        <span style={{ fontSize:11, color:'#555' }}>{secRate}%</span>
                      </div>
                      {openSec && (
                        <div style={{ marginLeft:24, padding:'4px 0 6px' }}>
                          {sec.teams && sec.teams.length ? sec.teams.map(team => {
                            const teamKey = `team-${team.id}`;
                            const openTeam = expanded[teamKey];
                            const teamRate = occupancyRate(team.current_headcount, team.planned_headcount);
                            return (
                              <div key={team.id} style={{ display:'flex', flexDirection:'column', borderBottom:'1px dotted #eee', padding:'4px 0', gap:4 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                  <Button type='text' size='small' onClick={()=> toggleKey(teamKey)} style={{ padding:'0 4px' }}>{openTeam ? '▾':'▸'}</Button>
                                  {editingName && editingName.id===team.id ? (
                                    <Space size={4}>
                                      <Input size='small' style={{ width:110 }} value={editingName.name} onChange={e=> setEditingName(n=> ({ ...n, name:e.target.value }))} />
                                      <Button icon={<SaveOutlined />} size='small' type='primary' loading={savingName} onClick={saveName} />
                                      <Button icon={<CloseOutlined />} size='small' onClick={cancelNameEdit} />
                                    </Space>
                                  ) : (
                                    <Space size={4}>
                                      <span>{team.name}</span>
                                      <Button icon={<EditOutlined />} size='small' type='text' onClick={()=> openNameEdit(team.id,'team',team.name)} />
                                    </Space>
                                  )}
                                  <Tag color='purple'>在岗 {team.current_headcount}</Tag>
                                  <Tag color='magenta' onClick={()=> openInlineEdit(team.id,'team',team.planned_headcount)} style={{ cursor:'pointer' }}>规划 {team.planned_headcount}</Tag>
                                  <div style={{ width:110 }}>
                                    <Progress percent={teamRate} size='small' status={teamRate>100?'exception':'active'} strokeColor={teamRate>100?'#ff4d4f':'#722ed1'} showInfo={false} />
                                  </div>
                                  <span style={{ fontSize:11, color:'#555' }}>{teamRate}%</span>
                                </div>
                                {openTeam && (
                                  <div style={{ marginLeft:24 }}>
                                    {team.positions && team.positions.length ? team.positions.map(pos => {
                                      const posRate = occupancyRate(pos.current_headcount, pos.planned_headcount);
                                      return (
                                        <div key={pos.id} style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:8, padding:'2px 0' }}>
                                          {editingName && editingName.id===pos.id ? (
                                            <Space size={4}>
                                              <Input size='small' style={{ width:100 }} value={editingName.name} onChange={e=> setEditingName(n=> ({ ...n, name:e.target.value }))} />
                                              <Button icon={<SaveOutlined />} size='small' type='primary' loading={savingName} onClick={saveName} />
                                              <Button icon={<CloseOutlined />} size='small' onClick={cancelNameEdit} />
                                            </Space>
                                          ) : (
                                            <Space size={4}>
                                              <span style={{ fontSize:12 }}>{pos.name}</span>
                                              <Button icon={<EditOutlined />} size='small' type='text' onClick={()=> openNameEdit(pos.id,'position',pos.name)} />
                                            </Space>
                                          )}
                                          <Tag color='cyan'>在岗 {pos.current_headcount}</Tag>
                                          <Tag color='processing' onClick={()=> openInlineEdit(pos.id,'position',pos.planned_headcount)} style={{ cursor:'pointer' }}>规划 {pos.planned_headcount}</Tag>
                                          <div style={{ width:90 }}>
                                            <Progress percent={posRate} size='small' status={posRate>100?'exception':'active'} strokeColor={posRate>100?'#ff4d4f':'#13c2c2'} showInfo={false} />
                                          </div>
                                          <span style={{ fontSize:11, color:'#555' }}>{posRate}%</span>
                                        </div>
                                      );
                                    }) : <span style={{ fontSize:11, color:'#999' }}>无岗位</span>}
                                  </div>
                                )}
                              </div>
                            );
                          }) : <span style={{ fontSize:11, color:'#999' }}>无班组</span>}
                        </div>
                      )}
                    </div>
                  );
                }) : <span style={{ fontSize:12, color:'#999' }}>无科室</span>}
              </div>
            )}
          </div>
        );
      })}

      {editingPlan && (
        <div style={{ marginTop:12, background:'#fff', border:'1px solid #e2e8f0', padding:12, borderRadius:6 }}>
          <Space>
            <span>调整 {editingPlan.type==='department'?'部门': editingPlan.type==='section'?'科室': editingPlan.type==='team'?'班组':'岗位'} 规划人数:</span>
            <InputNumber min={0} value={editingPlan.planned_headcount} onChange={v=> setEditingPlan(p=> ({ ...p, planned_headcount:v }))} />
            <Popconfirm title='确认保存?' onConfirm={savePlan} onCancel={()=> setEditingPlan(null)} okButtonProps={{ loading:savingPlan }}>
              <Button type='primary' size='small'>保存</Button>
            </Popconfirm>
            <Button size='small' onClick={()=> setEditingPlan(null)}>取消</Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default OrganizationHierarchyManager;
